/**
 * Labels API Client
 * Manages custom email labels
 */

import { getStoredSessionId } from './auth-client';

const CF_WORKER_URL = import.meta.env.VITE_CLOUDFLARE_WORKER_URL || '';
const API_BASE = CF_WORKER_URL ? `${CF_WORKER_URL}/api` : '/api';

export type SyncStatus = 'synced' | 'pending' | 'failed' | 'local_only';

export interface Label {
  id: string;
  user_id?: string;
  name: string;
  color: string;
  description?: string | null;
  email_count?: number;
  // Source indicator: 'ai' (system), 'gmail' (imported), 'user' (custom)
  source?: 'ai' | 'gmail' | 'user';
  // Gmail sync fields
  gmail_label_id?: string | null;
  sync_status?: SyncStatus;
  sync_error?: string | null;
  last_synced_at?: number | null;
  created_at?: number;
  updated_at?: number;
}

export interface CreateLabelInput {
  name: string;
  color?: string;
  description?: string;
  accessToken?: string; // For Gmail sync
}

export interface UpdateLabelInput {
  name?: string;
  color?: string;
  description?: string;
  accessToken?: string; // For Gmail sync
}

function getAuthHeaders(): HeadersInit {
  const sessionId = getStoredSessionId();
  return sessionId ? { 'X-Session-Id': sessionId } : {};
}

export interface FetchLabelsResponse {
  labels: Label[];
}

/**
 * Fetch all labels for the current user
 * All labels are user labels (AI auto-creates them during analysis)
 */
export async function fetchLabels(): Promise<FetchLabelsResponse> {
  const response = await fetch(`${API_BASE}/labels`, {
    credentials: 'include',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error('Failed to fetch labels');
  }

  const data = await response.json();
  return {
    labels: data.data.labels || [],
  };
}

/**
 * Create a new label
 */
export async function createLabel(input: CreateLabelInput): Promise<Label> {
  const response = await fetch(`${API_BASE}/labels`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error?.message || 'Failed to create label');
  }

  const data = await response.json();
  return data.data.label;
}

/**
 * Update a label
 */
export async function updateLabel(id: string, input: UpdateLabelInput): Promise<Label> {
  const response = await fetch(`${API_BASE}/labels/${id}`, {
    method: 'PUT',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error?.message || 'Failed to update label');
  }

  const data = await response.json();
  return data.data.label;
}

/**
 * Delete a label
 * If accessToken provided, also deletes from Gmail
 */
export async function deleteLabel(id: string, accessToken?: string): Promise<void> {
  const url = accessToken
    ? `${API_BASE}/labels/${id}?accessToken=${encodeURIComponent(accessToken)}`
    : `${API_BASE}/labels/${id}`;

  const response = await fetch(url, {
    method: 'DELETE',
    credentials: 'include',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error?.message || 'Failed to delete label');
  }
}

/**
 * Retry Gmail sync for a label
 * Creates Gmail label if missing, or updates if exists
 */
export async function retryLabelSync(id: string, accessToken: string): Promise<Label> {
  const response = await fetch(`${API_BASE}/labels/${id}/sync`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify({ accessToken }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error?.message || 'Failed to sync label');
  }

  const data = await response.json();
  return data.data.label;
}

/**
 * Apply a label to emails
 * If accessToken provided, also applies in Gmail
 */
export async function applyLabelToEmails(
  labelId: string,
  emailIds: string[],
  accessToken?: string
): Promise<{ applied: number; gmailApplied: number }> {
  const response = await fetch(`${API_BASE}/labels/${labelId}/emails`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify({ emailIds, accessToken }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error?.message || 'Failed to apply label');
  }

  const data = await response.json();
  return { applied: data.data.applied, gmailApplied: data.data.gmailApplied || 0 };
}

/**
 * Remove a label from emails
 */
export async function removeLabelFromEmails(labelId: string, emailIds: string[]): Promise<number> {
  const response = await fetch(`${API_BASE}/labels/${labelId}/emails`, {
    method: 'DELETE',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify({ emailIds }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error?.message || 'Failed to remove label');
  }

  const data = await response.json();
  return data.data.removed;
}

/**
 * Get labels for a specific email
 */
export async function getEmailLabels(emailId: string): Promise<Label[]> {
  const response = await fetch(`${API_BASE}/labels/emails/${emailId}`, {
    credentials: 'include',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error('Failed to fetch email labels');
  }

  const data = await response.json();
  return data.data.labels;
}

/**
 * Import labels from Gmail
 * Fetches all user labels from Gmail and syncs them to our database
 */
export async function importGmailLabels(accessToken: string): Promise<{
  imported: number;
  updated: number;
  skipped: number;
  total: number;
}> {
  const response = await fetch(`${API_BASE}/labels/import-gmail`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify({ accessToken }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error?.message || 'Failed to import Gmail labels');
  }

  const data = await response.json();
  return data.data;
}

/**
 * Sync email associations for a label from Gmail
 * Fetches all emails with this label from Gmail and links them in our database
 */
export async function syncLabelEmails(labelId: string, accessToken: string): Promise<{
  synced: number;
  total: number;
  inDatabase: number;
}> {
  const response = await fetch(`${API_BASE}/labels/${labelId}/sync-emails`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify({ accessToken }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error?.message || 'Failed to sync label emails');
  }

  const data = await response.json();
  return data.data;
}

/**
 * Backfill existing AI labels to user_labels
 * One-time migration to convert email_analysis.label entries to user_labels + email_labels
 */
export async function backfillLabels(): Promise<{
  labelsCreated: number;
  emailsLinked: number;
  message: string;
}> {
  const response = await fetch(`${API_BASE}/labels/backfill`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error?.message || 'Failed to backfill labels');
  }

  const data = await response.json();
  return data.data;
}
